"use client"

import { useCallback, useEffect, useRef, useState } from "react"

// A pane capped in height cuts its content mid-sentence with no edge in sight.
// Tracks whether anything is left below the fold so the pane can show a scroll
// affordance, and stops showing it once the reader reaches the end.
const SCROLL_HINT_THRESHOLD_PX = 8

// `contentKey` re-measures when what is rendered changes without any scrolling
// happening: a preview streaming in, or another theme selected in a rail.
export function useScrollHint(contentKey: string) {
  const ref = useRef<HTMLDivElement>(null)
  const [hasMore, setHasMore] = useState(false)

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    setHasMore(
      el.scrollHeight - el.scrollTop - el.clientHeight >
        SCROLL_HINT_THRESHOLD_PX,
    )
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    measure()
    el.addEventListener("scroll", measure, { passive: true })
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    if (el.firstElementChild) observer.observe(el.firstElementChild)
    return () => {
      el.removeEventListener("scroll", measure)
      observer.disconnect()
    }
  }, [measure, contentKey])

  return { ref, hasMore }
}
